/**
 * Générateur — Additionner deux nombres décimaux relatifs.
 * Attendu ID : 708 | Niveau : 5ème | Domaine : Nombres relatifs (cluster 4)
 *
 * Plan de cartes :
 *
 *   Carte « parenth » — Ex.2 Sommes de deux entiers (écriture avec parenthèses)
 *     texte_trou_saisie + qcm_unique. Tous les cas de signes en rotation
 *     (nn, pn, np, pp) + grands nombres (type (+100) + (-8)). Distracteurs
 *     QCM par misconception : signe inversé, valeurs absolues additionnées au
 *     lieu de soustraites (et réciproquement).
 *
 *   Carte « simplifie » — Ex.1 Écriture simplifiée mêlée
 *     texte_trou_saisie + qcm_unique. Premier terme sans parenthèses (-12 +
 *     (-15), -20 + 18…) — les deux écritures du cadrage. Le cas SOMME
 *     D'OPPOSÉS = 0 (21 + (-21)) est tiré une fois sur cinq : le cas clé.
 *
 *   Carte « decimaux » — Ex.3 Sommes de deux décimaux
 *     texte_trou_saisie. Une décimale (automatisme B1 sous-jacent).
 *
 *   Carte « temperature » — Ex.5 La température chute
 *     texte (saisie : durée pour atteindre un état — automatisme B2 :
 *     0 + … = -10) + qcm_unique (état après h heures, départ parfois non nul
 *     — « application d'une variation à un état », contexte du cadrage).
 *
 * Politique : résultats signés IMPOSÉS en consigne (+8, -5, 0 si nul) ;
 * tiret clavier ; arithmétique en dixièmes entiers.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 708;

const L_PARENTH = 'Sommes de deux entiers (avec parenthèses)';
const L_SIMPLIFIE = 'Sommes de deux entiers (écriture simplifiée)';
const L_DECIMAUX = 'Sommes de deux décimaux';
const L_TEMPERATURE = 'La température chute';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const frD = (d) => {
    const s = d < 0 ? '-' : '';
    const a = Math.abs(d);
    return s + (a % 10 === 0 ? String(a / 10) : `${Math.floor(a / 10)},${a % 10}`);
};
const signeD = (d) => (d === 0 ? '0' : d < 0 ? frD(d) : `+${frD(d)}`);

const CONSIGNE_RES = 'Écris le résultat avec son signe (ex. : -5 ou +8), ou 0 s\'il est nul.';

// ─── Tirages de paires (en dixièmes) ─────────────────────────

/** Paire d'ENTIERS selon le cas de signes ('nn'|'pn'|'np'|'pp'|'opposes'|'grand'). */
const tirerPaireEntiers = (cas) => {
    const u = () => randInt(3, 29) * 10;
    if (cas === 'opposes') { const a = u(); return [a, -a]; }
    if (cas === 'grand') {
        const g = randomChoice([100, 140, 200, 250]) * 10;
        const p = randInt(3, 19) * 10;
        return randomChoice([[g, -p], [-g, -p], [-g, p]]);
    }
    let a = u();
    let b = u();
    if (Math.abs(a) === Math.abs(b)) b += 10;
    if (cas === 'nn') return [-a, -b];
    if (cas === 'pp') return [a, b];
    return cas === 'pn' ? [a, -b] : [-a, b];
};

/** Paire de DÉCIMAUX à une décimale (jamais entiers tous les deux). */
const tirerPaireDecimaux = () => {
    const d = () => randInt(11, 99) + (randomChoice([true, false]) ? 0 : 0); // dixièmes 1,1..9,9
    let a = d();
    let b = d();
    if (a === b) b += 3;
    const signes = randomChoice([[1, 1], [-1, -1], [1, -1], [-1, 1]]);
    return [signes[0] * a, signes[1] * b];
};

/** Distracteurs de somme par misconception. */
const distracteursSomme = (a, b) => {
    const s = a + b;
    const absSum = Math.abs(a) + Math.abs(b);
    const absDiff = Math.abs(Math.abs(a) - Math.abs(b));
    const signe = s >= 0 ? 1 : -1;
    const cands = [signeD(-s), signeD(signe * absSum), signeD(signe * absDiff),
        signeD(-signe * absSum), signeD(s + 10), signeD(s - 10)];
    const out = [];
    cands.forEach(c => {
        if (out.length < 3 && c !== signeD(s) && !out.includes(c)) out.push(c);
    });
    return out;
};

// ═════════ Cartes 1-3 — sommes de deux termes ═════════

const CAS_PARENTH = ['nn', 'pn', 'np', 'pp', 'grand'];
const CAS_SIMPLIFIE = ['nn', 'pn', 'np', 'pp', 'opposes'];

const ecrParenth = (v) => `(${signeD(v)})`;
const ecrSimplifie = (v, premier) => (premier ? frD(v) : (v < 0 ? `(${signeD(v)})` : frD(v)));

const genSommeSaisie = (cases, ecriture, label) => () => {
    const [a, b] = tirerPaireEntiers(randomChoice(cases));
    const expr = ecriture === 'parenth'
        ? `${ecrParenth(a)} + ${ecrParenth(b)}`
        : `${ecrSimplifie(a, true)} + ${ecrSimplifie(b, false)}`;
    const phrase = `${expr} = {{${signeD(a + b)}}}`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Effectue le calcul.</b> ${CONSIGNE_RES}</p>`,
        { ...opts(label), mode: 'saisie' });
    payload._meta.params = { a, b };
    return payload;
};

const genSommeQcm = (cases, ecriture, label) => () => {
    const [a, b] = tirerPaireEntiers(randomChoice(cases));
    const expr = ecriture === 'parenth'
        ? `${ecrParenth(a)} + ${ecrParenth(b)}`
        : `${ecrSimplifie(a, true)} + ${ecrSimplifie(b, false)}`;
    const correct = signeD(a + b);
    const payload = makeQcm([correct, ...distracteursSomme(a, b)], correct,
        `<p><b>Que vaut ${expr} ?</b></p>`,
        opts(label));
    payload._meta.params = { a, b };
    return payload;
};

const genDecimauxSaisie = () => {
    const [a, b] = tirerPaireDecimaux();
    const phrase = `${ecrParenth(a)} + ${ecrParenth(b)} = {{${signeD(a + b)}}}`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Effectue le calcul.</b> ${CONSIGNE_RES}</p>`,
        { ...opts(L_DECIMAUX), mode: 'saisie' });
    payload._meta.params = { a, b };
    return payload;
};

// ═════════ Carte 4 — La température chute (Ex.5) ═════════

const genTempDuree = () => {
    const chute = randInt(2, 5);
    const heures = randInt(3, 9);
    const cible = -chute * heures;
    const payload = makeSaisie(String(heures),
        `<p>Il fait 0 °C et la température chute de ${chute} °C toutes les heures. `
        + `<b>Combien d'heures faudra-t-il pour que la température atteigne ${cible} °C ?</b></p>`,
        { ...opts(L_TEMPERATURE), suffixe: heures > 1 ? 'heures' : 'heure' });
    payload._meta.params = { chute, heures };
    return payload;
};

const genTempEtat = () => {
    const chute = randInt(2, 5);
    const h = randInt(4, 9);
    const depart = randomChoice([0, 0, randInt(-5, 5) * 10 / 10]); // départ parfois non nul
    const d0 = Math.round(depart) * 10; // dixièmes
    const arrivee = d0 - chute * 10 * h;
    const correct = signeD(arrivee);
    const cands = [signeD(-arrivee), signeD(d0 + chute * 10 * h), signeD(arrivee + 10), signeD(arrivee - 10)];
    const distracteurs = [];
    cands.forEach(c => {
        if (distracteurs.length < 3 && c !== correct && !distracteurs.includes(c)) distracteurs.push(c);
    });
    const payload = makeQcm([correct, ...distracteurs], correct,
        `<p>Il fait ${frD(d0)} °C et la température chute de ${chute} °C toutes les heures. `
        + `<b>Quelle sera la température dans ${h} heures ?</b></p>`,
        opts(L_TEMPERATURE));
    payload._meta.params = { chute, h, d0 };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    parenth_texte_trou_saisie: genSommeSaisie(CAS_PARENTH, 'parenth', L_PARENTH),
    parenth_qcm_unique: genSommeQcm(CAS_PARENTH, 'parenth', L_PARENTH),
    simplifie_texte_trou_saisie: genSommeSaisie(CAS_SIMPLIFIE, 'simplifie', L_SIMPLIFIE),
    simplifie_qcm_unique: genSommeQcm(CAS_SIMPLIFIE, 'simplifie', L_SIMPLIFIE),
    decimaux_texte_trou_saisie: genDecimauxSaisie,
    temperature_duree_texte: genTempDuree,
    temperature_etat_qcm_unique: genTempEtat,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM',
    duree_texte: 'Saisie', etat_qcm_unique: 'QCM',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'parenth', label: L_PARENTH, formatsList: buildFormats('parenth') },
    { id: 'simplifie', label: L_SIMPLIFIE, formatsList: buildFormats('simplifie') },
    { id: 'decimaux', label: L_DECIMAUX, formatsList: buildFormats('decimaux') },
    { id: 'temperature', label: L_TEMPERATURE, formatsList: buildFormats('temperature') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
